/**
 * Capsule recovery offer.
 *
 * The studio keeps a copy of the capsule as it is written, and this is where it
 * hands it back. It is a dialog rather than a banner because it is a *decision*
 * with two real answers — the creator's work, or a clean sheet — and a banner can
 * be scrolled past, which would leave the copy sitting on the device until
 * something else overwrote it.
 *
 * The offer never says "unsaved changes", because that is not what happened: a
 * session ended while the capsule was still being written. It says what is here,
 * how old it is, and the one thing that may not have come back with it — the cover
 * film, which is a local file a copy cannot carry.
 */
import {View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'

import {type CapsuleSnapshot} from '#/lib/capsuleRecovery'
import {describeSnapshotAge} from '#/lib/snapshotAge'
import {atoms as a, useTheme, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {Text} from '#/components/Typography'

export function CapsuleRecoveryDialog({
  control,
  snapshot,
  onRestore,
  onDiscard,
}: {
  control: Dialog.DialogControlProps
  /** The copy on offer, or `null` when there is nothing to hand back. */
  snapshot: CapsuleSnapshot | null
  onRestore: () => void
  onDiscard: () => void
}) {
  return (
    <Dialog.Outer
      control={control}
      nativeOptions={{preventExpansion: true}}
      webOptions={{alignCenter: true}}>
      <Dialog.Handle />
      {snapshot ? (
        <RecoveryBody
          snapshot={snapshot}
          onRestore={onRestore}
          onDiscard={onDiscard}
        />
      ) : null}
    </Dialog.Outer>
  )
}

function RecoveryBody({
  snapshot,
  onRestore,
  onDiscard,
}: {
  snapshot: CapsuleSnapshot
  onRestore: () => void
  onDiscard: () => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const control = Dialog.useDialogContext()

  const age = describeSnapshotAge(snapshot.savedAt)
  const when =
    age === 'moment'
      ? l`a moment ago`
      : age === 'minutes'
        ? l`a few minutes ago`
        : age === 'hours'
          ? l`earlier today`
          : age === 'days'
            ? l`a few days ago`
            : l`a while ago`

  const title = snapshot.capsule.title.trim() || l`Untitled capsule`
  const slides = snapshot.capsule.slides.length

  return (
    <Dialog.ScrollableInner
      style={web({maxWidth: 420})}
      label={l`Unfinished capsule`}>
      <View style={[a.gap_2xl]}>
        <View style={[a.gap_sm]}>
          <Text style={[a.font_bold, a.text_2xl]}>
            <Trans>Pick up your capsule?</Trans>
          </Text>
          <Text
            style={[t.atoms.text_contrast_medium, a.text_md, a.leading_snug]}>
            <Trans>
              {title} was still being written when this device last stopped. The
              copy taken {when} is still here, with {slides} slides on it.
            </Trans>
          </Text>
          {/*
            Stated plainly, and only where it is true of the copy: the creator's
            own slides come back, a file they attached to the cover does not. A
            warning that is always shown is a warning nobody reads on the day it
            matters, so this names the one thing actually at stake.
          */}
          <Text
            style={[t.atoms.text_contrast_medium, a.text_md, a.leading_snug]}
            testID="capsuleRecoveryFilmNote">
            <Trans>
              Everything you wrote comes back. A cover film you picked does not
              — it is uploaded when you publish.
            </Trans>
          </Text>
        </View>

        <View style={[a.gap_sm]}>
          <Button
            label={l`Restore this capsule`}
            accessibilityHint={l`Puts the copy back so you can carry on`}
            onPress={() => {
              onRestore()
              control.close()
            }}
            size="large"
            variant="solid"
            color="primary"
            testID="capsuleRecoveryRestoreBtn">
            <ButtonText>
              <Trans>Restore it</Trans>
            </ButtonText>
          </Button>
          <Button
            label={l`Discard the copy`}
            accessibilityHint={l`Starts a fresh capsule and deletes the copy`}
            onPress={() => {
              onDiscard()
              control.close()
            }}
            size="large"
            variant="solid"
            color="negative_subtle"
            testID="capsuleRecoveryDiscardBtn">
            <ButtonText>
              <Trans>Start fresh</Trans>
            </ButtonText>
          </Button>
        </View>
      </View>
    </Dialog.ScrollableInner>
  )
}
